/**
 * Pure functions deciding which language we speak to a member in.
 * Isomorphic — safe to use in both frontend and backend.
 *
 * The rules live here rather than at their call sites because PLA-628 shipped
 * the event-allowlist rule as behaviour inside one component, and a later
 * refactor deleted the component along with the rule.
 */

import {
  DEFAULT_LOCALE,
  SupportedTranslationLocaleSchema,
} from "../schemas/locale.schemas.js";
import type {
  LocaleCode,
  SupportedTranslationLocale,
} from "../schemas/locale.schemas.js";

/**
 * The language an event-scoped surface speaks: the candidate when the event
 * declares it, otherwise the event's primary language. Porter's reply, its
 * outbound `lang` stamp and the authenticated event pages all pass the member's
 * `preferredLocale`; anonymous event surfaces pass the browser locale.
 */
export function resolveEventLocale(
  candidate: LocaleCode | null,
  eventLanguages: readonly [
    SupportedTranslationLocale,
    ...SupportedTranslationLocale[],
  ],
): SupportedTranslationLocale {
  const declared = eventLanguages.find((language) => language === candidate);
  return declared ?? eventLanguages[0];
}

/**
 * The language a surface with no event to clamp to speaks: the first candidate
 * that has a dictionary, otherwise the default. Porter outside an event passes
 * `[detectedInbound, lastOutbound]`; a fresh web load passes `[browser]`.
 */
export function resolveSupportedLocale(
  candidates: readonly (LocaleCode | null)[],
): SupportedTranslationLocale {
  for (const candidate of candidates) {
    const parsed = SupportedTranslationLocaleSchema.safeParse(candidate);
    if (parsed.success) {
      return parsed.data;
    }
  }
  return DEFAULT_LOCALE;
}
